"use client";

import { useMemo, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  ArrowUpIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  LinkIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/20/solid";
import ComboBox from "../ComboBox";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CreatableSelect from "react-select/creatable";
import { animatedComponents, customStyles } from "../../utils/constants";
import { uploadReportSchema } from "../../Schema/reportUploadSchema";
import { getBlobSasToken } from "../../api/api-methods";
import { BlockBlobClient } from "@azure/storage-blob";
import { v4 as uuidv4 } from "uuid";
import { uploadFile } from "../../utils/helperMethods";
import { useCreateDocument } from "../../custom-hooks/queries";
import NotificationModal from "../NotificationModal";
import ButtonLoader from "../ButtonLoader";
import ReportUploadDropdown from "../ReportUploadDropdown";
import { useDecodedToken } from "../../custom-hooks/useToken";

export default function ReportUploadDrawer({
  open,
  setOpen,
  dataVqa,
  refetch,
  activityRefetch,
  vqaDetailsRefetch,
  documentType,
  allDocuments,
  sponsors,
  allowFlexibleReportTypes = false,
}) {
  const methods = useForm({
    resolver: yupResolver(uploadReportSchema),
    mode: "onTouched",
    defaultValues: {},
  });
  const {
    handleSubmit,
    watch,
    setValue,
    register,
    formState: { errors },
    reset,
    control,
  } = methods;

  const newGuid = uuidv4();
  const newGuid2 = uuidv4();
  const { mutate: createDocumentMutation, isError } = useCreateDocument();
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef();
  const [files, setFiles] = useState("");
  const [fileName, setFileName] = useState("");
  const [notificationTrigger, setNotificationTrigger] = useState(false);
  const [message, setMessage] = useState(false);
  const [error, setError] = useState(false);

  const { decoded } = useDecodedToken();

  const optionsForMultiSelectSponsor = useMemo(() => {
    return sponsors
      ?.filter((item) => item?.sponsor_status !== "Canceled Assessment") // Adjust the status check as needed
      ?.map((item) => ({
        id: item?.sponsor_id,
        name: item?.sponsor_name,
        email: item?.sponsor_contact_email,
        isDisabled: item?.sponsor_status === "Canceled Assessment", // Optional: explicitly mark as disabled
      }));
  }, [sponsors]);

  const handleButtonClick = () => {
    // Trigger the file input dialog programmatically
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    setFileName(selectedFile?.name);
    setFiles(selectedFile);
  };

  const onSubmit = async (data) => {
    if (!files) {
      setNotificationTrigger(true);
      setMessage("Please select a file to upload");
      return;
    }

    setIsLoading(true);
    const blobUrl = await uploadFile(files, newGuid, "documents");

    if (!blobUrl || typeof blobUrl !== "string") {
      setNotificationTrigger(true);
      setMessage(
        "Upload failed (no blob URL). Local: set REACT_APP_SKIP_BLOB_UPLOAD in .env.local if you're skipping real blob upload."
      );
      setIsLoading(false);
      return;
    }

    const transformedCapabilities = data?.serviceCategories?.map((service) => ({
      rfi_id: crypto.randomUUID(),
      rfi_name: service.label,
    }));

    const convertToString = data?.serviceCategories
      ?.map((item) => item?.label)
      ?.join(" | ");
    const documentMetadataPayload = {
      document_name: files?.name,
      document_version: "1.0",
      document_url: blobUrl.includes("?")
        ? blobUrl.split("?")[0]
        : blobUrl,
      provider_name: dataVqa?.provider?.provider_name,
      provider_id: dataVqa?.provider?.provider_id,
      comments: data?.comments,
      capabilities: [],
      entity_type: "VQA",
      entity_id: dataVqa?.vqa?.vqa_id,
      document_sub_type: data?.reportType.name,
      tags: convertToString,
      capabilities: dataVqa?.capabilities,
      document_location: dataVqa?.provider?.provider_address1,
      document_city: dataVqa?.provider?.provider_city,
      document_state: dataVqa?.provider?.provider_state,
      document_zip: dataVqa?.provider?.provider_zip,
      document_country: dataVqa?.provider?.provider_country,
      sponsor_id: data?.sponsor?.id,
      sponsor_name: data?.sponsor?.name,
      sponsor_contact_email: data?.sponsor?.email,
      activity_username: decoded?.name,
      vqa_request_id: dataVqa?.vqa_request_id,
    };

    createDocumentMutation(documentMetadataPayload, {
      onSuccess: () => {
        setNotificationTrigger(true);
        setMessage("Report has successfully been uploaded");
        setTimeout(() => {
          setOpen(false);
        }, 5000);
        reset();
        setFileName("");
        refetch();
        activityRefetch();
        vqaDetailsRefetch();
        setIsLoading(false);
      },
      onError: () => {
        setNotificationTrigger(true);
        setMessage("Error in uploading file");

        setIsLoading(false);
      },
    });
  };

  const reportTypesList = [
    {
      name: "Sponsor Draft Report",
      id: "Sponsor Draft Report",
    },
    {
      name: "Vendor Draft Report",
      id: "Provider Draft Report",
    },
    {
      name: "Sponsor Observation Report",
      id: "CAPA Response Report",
    },
    {
      name: "Final Report",
      id: "Final Report",
    },
    {
      name: "Report Anonymized",
      id: "Report Anonymized",
    },
  ];

  const reportType = watch("reportType");

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-50">
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden bg-opacity-40 backdrop-blur-sm backdrop-filter">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-md  transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <form
                className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="h-0 flex-1 overflow-y-auto">
                  <div className="bg-indigo-600 px-4 py-6 sm:px-6">
                    <div className="flex items-center justify-between">
                      <DialogTitle className="text-base font-semibold text-white">
                        Report Upload
                      </DialogTitle>
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="relative rounded-md bg-indigo-700 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon aria-hidden="true" className="size-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col justify-between">
                    <form>
                      <div className="px-4 sm:px-6">
                        <div className="space-y-6 pb-5 pt-6">
                          <div>
                            <label
                              htmlFor="project-name"
                              className="block text-sm/6 font-medium text-gray-900"
                            >
                              Report Type
                            </label>
                            <div className="mt-2">
                              {" "}
                              <Controller
                                name="reportType" // Matches the Yup schema
                                control={control}
                                render={({ field }) => (
                                  <ReportUploadDropdown
                                    data={reportTypesList}
                                    placeHolder="Select Report Type"
                                    setSelected={(value) =>
                                      value && setValue("reportType", value)
                                    }
                                    selected={watch("reportType")}
                                    width={"w-full"}
                                    inputWidth={"w-full"}
                                    documentType={documentType}
                                    allDocuments={allDocuments}
                                    allowFlexibleReportTypes={
                                      allowFlexibleReportTypes
                                    }
                                  />
                                )}
                              />
                            </div>
                            {errors && (
                              <span className="text-xs text-red-500">
                                {errors?.reportType?.message}
                              </span>
                            )}
                          </div>

                          {(reportType?.id === "Sponsor Draft Report" ||
                            reportType?.id === "Final Report") && (
                            <div>
                              <label
                                htmlFor="project-name"
                                className="block text-sm/6 font-medium text-gray-900"
                              >
                                Sponsor
                              </label>
                              <div className="mt-2">
                                {" "}
                                <Controller
                                  name="sponsor"
                                  control={control}
                                  render={({ field }) => (
                                    <ComboBox
                                      data={optionsForMultiSelectSponsor}
                                      placeHolder="Select Sponsor"
                                      setSelected={(value) =>
                                        value && setValue("sponsor", value)
                                      }
                                      selected={watch("sponsor")}
                                      width={"w-full"}
                                      inputWidth={"w-full"}
                                    />
                                  )}
                                />
                              </div>
                              {errors && (
                                <span className=" text-xs text-red-500">
                                  {errors?.sponsor?.message}
                                </span>
                              )}
                            </div>
                          )}
                          <div>
                            <label
                              htmlFor="project-description"
                              className="block text-sm/6 font-medium text-gray-900"
                            >
                              Tag Fields
                            </label>
                            <div className="mt-2">
                              <Controller
                                name="serviceCategories"
                                control={control}
                                defaultValue={[]} // Set a default value to avoid undefined errors.
                                rules={{
                                  validate: (value) =>
                                    Array.isArray(value) && value.length > 0
                                      ? true
                                      : "Please select at least one category.",
                                }}
                                render={({ field, fieldState: { error } }) => (
                                  <div>
                                    <CreatableSelect
                                      {...field}
                                      closeMenuOnSelect={false}
                                      isMulti
                                      components={{ DropdownIndicator: null }}
                                      value={field.value || []}
                                      onChange={(selected) =>
                                        field.onChange(selected)
                                      }
                                      onCreateOption={(inputValue) => {
                                        const newOption = {
                                          label: inputValue,
                                          value: inputValue,
                                        };
                                        const newValue = [
                                          ...(field.value || []),
                                          newOption,
                                        ];
                                        field.onChange(newValue);
                                      }}
                                      isClearable={field.value?.length > 1}
                                      className="w-full text-sm font-normal"
                                      styles={{
                                        ...customStyles,
                                        control: (base) => ({
                                          ...base,
                                          boxShadow: "none",
                                          borderColor: "lightgray",
                                          borderRadius: "5px",
                                          "&:focus": {
                                            borderColor: "lightgray",
                                            boxShadow: "none",
                                          },
                                        }),
                                      }}
                                      placeholder="Type to create and select tags..."
                                      noOptionsMessage={() => null} // No dropdown unless creating
                                    />

                                    {/* {error && (
                                      <span className="text-red-500">
                                        {error.message}
                                      </span>
                                    )} */}
                                  </div>
                                )}
                              />
                            </div>
                            <div className="mt-2 space-x-2 flex justify-end flex-wrap whitespace-break-spaces">
                              {watch("serviceCategories")?.length > 0 &&
                                watch("serviceCategories")?.map(
                                  (item, index) => (
                                    <>
                                      <span
                                        key={index}
                                        className="my-1 inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"
                                      >
                                        <div key={item.value}>{item.label}</div>
                                      </span>{" "}
                                    </>
                                  )
                                )}
                            </div>
                          </div>
                          <div>
                            <label
                              htmlFor="project-description"
                              className="block text-sm/6 font-medium text-gray-900"
                            >
                              Comment
                            </label>
                            <div className="mt-2">
                              <input
                                id="project-description"
                                name="project-description"
                                rows={3}
                                {...register("comments")}
                                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border border-gray-300 focus:ring-0 focus:outline-none placeholder:text-gray-400 sm:text-sm/6"
                                defaultValue={""}
                              />
                            </div>
                          </div>
                          <div>
                            <label
                              htmlFor="project-description"
                              className="block text-sm/6 font-medium text-gray-900"
                            >
                              Upload Report
                            </label>
                            <div className=" flex justify-center items-center mt-2 border border-dashed p-5 rounded-[3px] border-gray-300">
                              <input
                                type="file"
                                id="file-upload"
                                name="file-upload"
                                className="hidden"
                                onChange={handleFileChange}
                                ref={(e) => {
                                  register("file", {
                                    required: "A file is required",
                                    validate: {
                                      isPDF: (file) =>
                                        (file &&
                                          file.type === "application/pdf") ||
                                        "Only PDF files are allowed",
                                    },
                                  });

                                  fileInputRef.current = e;
                                }}
                              />
                              {errors && errors?.file?.message}

                              <button
                                onClick={handleButtonClick}
                                type="button"
                                className="flex justify-center items-center flex-col space-y-3"
                              >
                                <ArrowUpTrayIcon className="bg-gray-300 rounded-full p-3 h-12 w-12 font-bold" />
                                <h1 className="font-semibold text-md">
                                  {fileName.length > 0
                                    ? fileName
                                    : "Click here to upload a file"}
                                </h1>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="pb-6 pt-4"></div>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="flex shrink-0 justify-end px-4 py-4">
                  <button
                    type="button"
                    onClick={() => {
                      reset();
                      setFileName("");
                      setOpen(false);
                    }}
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={(e) => {
                      e.preventDefault();
                      if (fileName?.length > 0) {
                        handleSubmit((data) => onSubmit(data))();
                      } else {
                        setError(true);
                        setMessage("Please select a file first");
                        setNotificationTrigger(true);
                      }
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? <ButtonLoader /> : "Save"}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </div>
      </div>

      <NotificationModal
        open={notificationTrigger}
        setOpen={setNotificationTrigger}
        desc={message}
        title={isError || error ? "Error" : "Success"}
        error={isError || error}
      />
    </Dialog>
  );
}
